import { Pill } from "@gadgetinc/widgets";
import { Body } from "@gadgetinc/widgets/src/typography/Body";
import { useStyletron } from "baseui";
import { HeadingMedium } from "baseui/typography";
import React from "react";
import { DocsMarkdownRenderer } from "../../../../src/components/MarkdownRenderer";
import BulletIcon from "./BulletIcon";

function ChangelogIndexIndicator() {
  const [css, $theme] = useStyletron();

  return (
    <div
      className={css({
        display: "flex",
        alignItems: "center",
        alignSelf: "stretch",
        flexDirection: "column",
      })}
    >
      <BulletIcon />
      <div
        className={css({
          width: "1px",
          height: "100%",
          backgroundColor: $theme.colors.primary200,
        })}
      />
    </div>
  );
}

function DateLabel({ date }: { date: Date }) {
  const [_, $theme] = useStyletron();

  return (
    <Body
      $style={{
        fontFamily: "Helvetica",
        fontSize: $theme.sizing.scale650,
      }}
    >
      {new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })}
    </Body>
  );
}

function PillGroup({ breaking, repository, area, label }: { breaking?: boolean; repository?: string; area?: string; label?: string }) {
  const [css, $theme] = useStyletron();

  return (
    <div
      className={css({
        display: "flex",
        alignItems: "flex-start",
        alignContent: "flex-start",
        gap: $theme.sizing.scale400,
        flex: "1 0 0",
        alignSelf: "stretch",
        flexWrap: "wrap",
      })}
    >
      {breaking && <Pill $color="yellow">⚠️ breaking change</Pill>}
      {repository && <Pill>{repository.toLowerCase()}</Pill>}
      {area && <Pill>{area.toLowerCase()}</Pill>}
      {label && <Pill>{label.toLowerCase()}</Pill>}
    </div>
  );
}

export type Changelog = {
  id: string;
  title: string;
  slug: string;
  label?: string;
  area?: string;
  content: {
    markdown: string;
  };
  pullRequest: {
    repository?: string;
  };
  breaking: boolean;
  date: Date;
};

export default function ({ changelog }: { changelog: Changelog }) {
  const [css, $theme] = useStyletron();

  return (
    <div
      className={css({
        display: "flex",
        alignItems: "flex-start",
        gap: $theme.sizing.scale800,
        alignSelf: "stretch",
      })}
    >
      <ChangelogIndexIndicator />
      <div
        className={css({
          display: "flex",
          gap: $theme.sizing.scale800,
          paddingBottom: $theme.sizing.scale1200,
          "@media (max-width: 1485px)": {
            flexDirection: "column",
            gap: $theme.sizing.scale600,
          },
        })}
      >
        <section
          className={css({
            display: "flex",
            flexDirection: "column",
            width: "240px",
            alignItems: "flex-start",
            gap: $theme.sizing.scale600,
            alignSelf: "stretch",
            "@media (max-width: 1485px)": {
              width: "100%",
              gap: $theme.sizing.scale500,
            },
          })}
        >
          <DateLabel date={changelog.date} />
          <PillGroup
            breaking={changelog.breaking}
            repository={changelog.pullRequest?.repository}
            area={changelog.area}
            label={changelog.label}
          />
        </section>
        <section
          className={css({
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: $theme.sizing.scale600,
            flex: "1 0 0",
          })}
        >
          <HeadingMedium>{changelog.title}</HeadingMedium>
          <Body>
            <DocsMarkdownRenderer markdown={changelog.content.markdown} />
          </Body>
        </section>
      </div>
    </div>
  );
}
